import React, { useMemo } from 'react';
import { dayjs } from '@koob/dates';
import KoobSeparatorLine from '@koob/ui/src/components/KoobSeparatorLine';
import TripDayReadOnly from '../TripDayReadOnly/TripDayReadOnly';
import TripReadOnlyServicesBand from '../TripDayReadOnly/TripReadOnlyServicesBand';
import { AdditionalServicesBandScopeEnum } from '../AdditionalServicesBand';
import TripVersionCompareFrame from './TripVersionCompareFrame';
import TripVersionCompareLoading from './TripVersionCompareLoading';
import { useTripVersionPreview } from '../../hooks/useTripVersionPreview';
import {
  useCompareWithVersion,
  useTripFilters,
} from '../../hooks/useTripStoreSelectors';
import { getStayRooms, getVersionOutline } from '../../logic/versionCompare';

interface TripVersionCompareRowHeaderProps {
  variant?: 'departure';
  title: string;
  date: unknown;
  location: string | null;
}

interface TripVersionCompareProps {
  tripId: string;
  translation: (key: string, options?: Record<string, any>) => string;
  // The arrival/departure row is drawn by the app: it comes from the map
  // screen, which is not shared yet.
  renderRowHeader: (props: TripVersionCompareRowHeaderProps) => React.ReactNode;
}

/**
 * Reads the version picked in the version popover beside the live trip, with
 * the same structure: one frame per experience, its days inside, its global
 * services closing it.
 */
export default function TripVersionCompare({
  tripId,
  translation: t,
  renderRowHeader,
}: TripVersionCompareProps) {
  const compareWithVersion = useCompareWithVersion();
  const tripFilters: any = useTripFilters();
  const { trip, days, experiences, hotels, isLoading, getFactsheetUrl } =
    useTripVersionPreview({ tripId, versionId: compareWithVersion?.id });

  const startTitle = tripFilters?.startLocation?.title;
  const endTitle = tripFilters?.endLocation?.title;
  const { blocks, arrivalLocation, departureLocation } = useMemo(
    () => getVersionOutline(days, experiences, startTitle, endTitle),
    [days, experiences, startTitle, endTitle],
  );

  const departureDate = tripFilters.departureDate;

  const renderDayAt = (dayIndex: number, frame: any | null) => {
    const day = days[dayIndex];

    return (
      <React.Fragment key={dayIndex}>
        <TripDayReadOnly
          dayIndex={dayIndex}
          date={dayjs(departureDate).add(dayIndex, 'days')}
          departureDate={departureDate}
          days={days}
          experiences={experiences}
          experience={day?.experienceId ? experiences[day.experienceId] : null}
          experienceDayIndex={day?.experienceDayIndex}
          extras={day?.selectedExtras ?? []}
          hotel={day?.hotelId ? hotels[day.hotelId] : null}
          hotelBookedOnMyOwn={day?.hotelBookedOnMyOwn ?? false}
          frame={frame}
          stayRooms={getStayRooms(days, dayIndex)}
          t={t}
        />
      </React.Fragment>
    );
  };

  if (isLoading) {
    return (
      <div className="mt-5">
        <TripVersionCompareLoading />
      </div>
    );
  }

  return (
    <div className="mt-5">
      {renderRowHeader({
        title: t('compose.arrival'),
        date: departureDate,
        location: arrivalLocation,
      })}

      <div>
        {trip?.tripDays?.length === 0 && (
          <div className="text-gray-500 text-sm">
            {t('compose.versions.emptyDays')}
          </div>
        )}

        <KoobSeparatorLine />

        {blocks.map(({ frame, dayIndices }) => {
          const firstDayIndex = dayIndices[0];

          if (!frame) {
            return (
              <div key={`days-${firstDayIndex}`} className="mb-3">
                {dayIndices.map((i) => renderDayAt(i, null))}
              </div>
            );
          }

          const experience = experiences[frame.experienceId];

          return (
            <div key={`frame-${firstDayIndex}`} className="mb-3">
              <TripVersionCompareFrame
                frame={frame}
                days={days}
                experience={experience}
                factsheetUrl={getFactsheetUrl(experience) ?? undefined}
                translation={t}
                renderGlobalServices={(extras) => (
                  <TripReadOnlyServicesBand
                    scope={AdditionalServicesBandScopeEnum.GLOBAL}
                    extras={extras}
                    departureDate={departureDate}
                    frame={frame}
                    entries={experience?.extras}
                    t={t}
                  />
                )}
              >
                {dayIndices.map((i) => renderDayAt(i, frame))}
              </TripVersionCompareFrame>
            </div>
          );
        })}

        {/* The version closes on the same row as the live trip. */}
        {renderRowHeader({
          variant: 'departure',
          title: t('compose.departure'),
          date: dayjs(departureDate).add(Math.max(days.length - 1, 0), 'days'),
          location: departureLocation,
        })}
      </div>
    </div>
  );
}
